import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api, can, getUser, heldForApproval, roleLabel } from '../lib/api';
import { Card, Empty, ErrorNote, Field, Loading, Modal, PageHead, Pill, useResource, useToast } from '../components/ui';
import { RoleMatrix, type RoleRow, type ScreenDef } from '../components/RoleMatrix';
import { dateTime, day } from '../lib/format';
import { RowActions } from '../components/RowActions';
import '../styles/nominations.css';

/**
 * CM-9 — People & access, plus deployment settings.
 *
 * Nobody here has a password. An owner invites a person by email; the person
 * signs in with a code sent to that email, or with the one-time link in the
 * invitation. Deactivating someone ends their sessions the same second.
 */

type RoleKey = string;

interface Person {
  userId: string;
  email: string;
  name: string;
  role: RoleKey;
  status: 'active' | 'disabled';
  createdAt: string;
  invitedBy: string | null;
  lastLoginAt: string | null;
  deactivatedAt: string | null;
  hasPassword: boolean;
  /** Two-factor sign-in is on. */
  mfa?: boolean;
}

export type SettingsSection = 'users' | 'roles' | 'languages' | 'channels' | 'security';

export default function Settings({ section = 'users' }: { section?: SettingsSection }) {
  if (section === 'languages') return <><PageHead title="Languages" /><LanguagesCard canEdit={can(getUser(), 'languages:edit')} /></>;
  if (section === 'channels') return <><PageHead title="Channels" />{can(getUser(), 'channels:edit') ? <ChannelsCard /> : <Card><Empty>This role can view channels but not change them.</Empty></Card>}</>;
  if (section === 'security') return <SecurityPage />;
  if (section === 'roles') return <RolesPage />;
  return <UsersPage />;
}

/** Roles: who can do what, one table of screens and actions against the tenant's roles. */
function RolesPage() {
  const me = getUser();
  const [reload, setReload] = useState(0);
  const editsRoles = can(me, 'users:roles');
  const rolesRes = useResource<{ screens: ScreenDef[]; roles: RoleRow[] }>(() => api.get('/v1/roles'), [reload]);
  const roles = rolesRes.data?.roles ?? [];
  return (
    <>
      <PageHead title="Roles" />
      <ErrorNote error={rolesRes.error} />
      {rolesRes.loading && !rolesRes.data && <Loading what="Loading roles" />}
      {rolesRes.data && (
        <Card title="Roles">
          <div className="table-scroll">
            <table className="data">
              <thead>
                <tr><th>Role</th><th>Kind</th><th className="num">People</th><th className="num">Permissions</th></tr>
              </thead>
              <tbody>
                {roles.map((r) => (
                  <tr key={r.id}>
                    <td><strong>{r.name}</strong>{r.description ? <div className="small muted">{r.description}</div> : null}</td>
                    <td className="small">{r.system ? 'Built in' : 'Custom'}</td>
                    <td className="num">{r.users}</td>
                    <td className="num">{r.permissions.length}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
      {rolesRes.data && (
        <RoleMatrix roles={roles} screens={rolesRes.data.screens} canEdit={editsRoles} onChanged={() => setReload((n) => n + 1)} />
      )}
    </>
  );
}

function UsersPage() {
  const me = getUser();
  const toast = useToast();
  const [inviting, setInviting] = useState(false);
  const [issued, setIssued] = useState<{ email: string; link: string; days: number; warning?: string } | null>(null);
  const [reload, setReload] = useState(0);
  const manages = can(me, 'users:edit');
  const rolesRes = useResource<{ screens: ScreenDef[]; roles: RoleRow[] }>(() => api.get('/v1/roles'), [reload]);
  const roles = rolesRes.data?.roles ?? [];

  const people = useResource<{ users: Person[] }>(
    () => api.get('/v1/users'), [reload],
  );
  const key = useResource<{ keyId: string; publicKeyPem: string }>(() => api.get('/v1/exports/public-key'));

  async function act(path: string, body: unknown, ok: string) {
    try {
      const res = await api.post(path, body);
      setReload((n) => n + 1);
      toast(heldForApproval(res) ? 'Sent for approval. A second person must approve this change on the Approvals page.' : ok, 'ok');
    } catch (err) {
      toast(err instanceof Error ? err.message : String(err), 'bad');
    }
  }

  const rows = people.data?.users ?? [];
  const active = rows.filter((p) => p.status === 'active');
  const disabled = rows.filter((p) => p.status !== 'active');

  return (
    <>
      <PageHead
        title="Users & roles"
      >
        {manages && <button className="primary" onClick={() => setInviting(true)}>Invite user</button>}
      </PageHead>

      {true ? (
        <Card title="Users">
          {people.loading && <Loading what="Loading users" />}
          <ErrorNote error={people.error} />
          {!people.loading && !rows.length && <Empty>No users.</Empty>}
          {!!rows.length && (
            <div className="table-scroll">
              <table className="data">
                <thead>
                  <tr>
                    <th>Person</th>
                    <th>Role</th>
                    <th>Access</th>
                    <th>Last sign-in</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {[...active, ...disabled].map((p) => {
                    const isMe = p.userId === me?.userId;
                    return (
                      <tr key={p.userId} className={p.status !== 'active' ? 'muted' : ''}>
                        <td>
                          <strong>{p.name || p.email}</strong>
                          {isMe && <span className="pill tag" style={{ marginLeft: 8 }}>You</span>}
                          <div className="small muted mono">{p.email}</div>
                        </td>
                        <td>
                          <select
                            value={p.role}
                            disabled={!manages || isMe || p.status !== 'active'}
                            onChange={(e) => act(`/v1/users/${p.userId}/role`, { role: e.target.value }, `${p.email} is now ${roles.find((r) => r.key === e.target.value)?.name ?? e.target.value}.`)}
                          >
                            {roles.filter((r) => r.key !== 'owner' || me?.role === 'owner' || p.role === 'owner').map((r) => <option key={r.key} value={r.key}>{r.name}</option>)}
                          </select>
                        </td>
                        <td>
                          {p.status === 'active'
                            ? <span className="muted small">Active</span>
                            : <Pill tone="bad">Deactivated</Pill>}
                          {p.hasPassword && (
                            <div className="small muted">Legacy password</div>
                          )}
                          {p.status === 'active' && (
                            <div className="small muted">{p.mfa ? 'Two-factor on' : 'Two-factor off'}</div>
                          )}
                        </td>
                        <td className="small muted">
                          {p.lastLoginAt ? day(p.lastLoginAt) : <span>never</span>}
                        </td>
                        <td className="right nowrap">
                          {!manages ? null : p.status === 'active' ? (
                            <>
                              <button
                                className="small"
                                onClick={async () => {
                                  try {
                                    const res = await api.post<{ link: string; validForDays: number }>(`/v1/users/${p.userId}/signin-link`);
                                    setIssued({ email: p.email, link: res.link, days: res.validForDays });
                                  } catch (err) {
                                    toast(err instanceof Error ? err.message : String(err), 'bad');
                                  }
                                }}
                              >
                                Sign-in link
                              </button>{' '}
                              {!isMe && (
                                <RowActions more={[
                                  ...(me?.role === 'owner' && p.mfa ? [{
                                    label: 'Reset two-factor',
                                    onClick: () => {
                                      if (window.confirm(`Reset two-factor sign-in for ${p.email}? Their sessions end and they set up an authenticator again at the next sign-in.`)) {
                                        act(`/v1/users/${p.userId}/mfa/reset`, {}, `Two-factor sign-in reset for ${p.email}.`);
                                      }
                                    },
                                  }] : []),
                                  { label: 'Deactivate', danger: true, onClick: () => act(`/v1/users/${p.userId}/deactivate`, {}, `${p.email} deactivated.`) },
                                ]} />
                              )}
                            </>
                          ) : (
                            <button
                              className="small"
                              onClick={() => act(`/v1/users/${p.userId}/reactivate`, {}, `${p.email} reactivated.`)}
                            >
                              Reactivate
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      ) : (
        null
      )}

      <ErrorNote error={rolesRes.error} />

      {inviting && (
        <InviteModal
          roles={roles}
          canInviteOwner={me?.role === 'owner'}
          onClose={() => setInviting(false)}
          onInvited={(email, link, warning) => {
            setInviting(false);
            setReload((n) => n + 1);
            if (!link) { toast(`Sent for approval. The invitation for ${email} goes out once a second person approves it.`, 'ok'); return; }
            setIssued({ email, link, days: 7, ...(warning ? { warning } : {}) });
          }}
        />
      )}

      {issued && (
        <Modal
          title={`Sign-in link for ${issued.email}`}
          description="Single use. Alternatively, the user can sign in with their email address and a code."
          onClose={() => setIssued(null)}
          footer={<button className="primary" onClick={() => setIssued(null)}>Done</button>}
        >
          {issued.warning && (
            <div className="note warn" style={{ marginBottom: 12 }}>
              <strong>Invitation email not sent.</strong> The account exists. Send this link yourself.
              <div className="small" style={{ marginTop: 4 }}>
                {/verify a domain|testing emails|own email address/i.test(issued.warning)
                  ? 'The mail service is in test mode and delivers only to its own account address. Verify a sending domain with it, or add an email channel under Channels.'
                  : issued.warning.replace(/\{.*$/s, '').slice(0, 160)}
              </div>
            </div>
          )}
          <div className="note">
            <div className="mono linkblock">{issued.link}</div>
            <button
              className="small"
              onClick={() => navigator.clipboard.writeText(issued.link).then(
                () => toast('Link copied', 'ok'), () => toast('Could not copy. Select it manually.', 'bad'),
              )}
            >
              Copy link
            </button>
            <div className="small muted" style={{ marginTop: 8 }}>Valid for {issued.days} day(s).</div>
          </div>
        </Modal>
      )}
    </>
  );
}

function InviteModal({
  roles,
  canInviteOwner, onClose, onInvited,
}: {
  roles: RoleRow[];
  canInviteOwner: boolean;
  onClose: () => void;
  onInvited: (email: string, link: string, warning?: string) => void;
}) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<RoleKey>('compliance_admin');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function invite() {
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<{
        user: { email: string }; link: string; emailDelivered: boolean; emailError?: string;
      }>('/v1/users', { email, name, role });
      if (heldForApproval(res)) { onInvited(email, ''); return; }
      onInvited(res.user.email, res.link, res.emailDelivered ? undefined : (res.emailError ?? 'delivery refused'));
    } catch (err) {
      setError((err as Error).message);
      setBusy(false);
    }
  }

  return (
    <Modal
      title="Invite user"
      description="The user receives a single-use sign-in link by email."
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose}>Cancel</button>
          <button className="primary" onClick={invite} disabled={busy || !email.includes('@')}>
            {busy ? 'Inviting…' : 'Send invitation'}
          </button>
        </>
      }
    >
      <div className="row">
        <Field label="Work email"><input type="email" value={email} autoFocus onChange={(e) => setEmail(e.target.value)} /></Field>
        <Field label="Name"><input type="text" value={name} onChange={(e) => setName(e.target.value)} /></Field>
      </div>
      <Field label="Role" help={roles.find((r) => r.key === role)?.description}>
        <select value={role} onChange={(e) => setRole(e.target.value as RoleKey)}>
          {roles.filter((r) => canInviteOwner || r.key !== 'owner').map((r) => (
            <option key={r.key} value={r.key}>{r.name}</option>
          ))}
        </select>
      </Field>
      <ErrorNote error={error} />
    </Modal>
  );
}

interface LanguageSettings {
  languages: string[];
  catalogue: Array<{ code: string; endonym: string; english: string; script: string; interfacePack: boolean }>;
}

/**
 * Which languages the organisation offers. Every purpose and notice then shows
 * its coverage against this list, and the widget offers exactly these.
 */
function LanguagesCard({ canEdit }: { canEdit: boolean }) {
  const toast = useToast();
  const settings = useResource<LanguageSettings>(() => api.get('/v1/settings/languages'));
  const [chosen, setChosen] = useState<string[] | null>(null);
  const [busy, setBusy] = useState(false);
  const current = chosen ?? settings.data?.languages ?? ['en'];
  const dirty = chosen !== null && JSON.stringify([...chosen].sort()) !== JSON.stringify([...(settings.data?.languages ?? [])].sort());

  async function save() {
    setBusy(true);
    try {
      await api.put('/v1/settings/languages', { languages: current });
      toast('Languages saved.', 'ok');
      setChosen(null);
      settings.reload();
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title="Languages offered">
      <ErrorNote error={settings.error} />
      {settings.loading && !settings.data && <Loading what="Loading languages" />}
      {settings.data && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '4px 16px' }}>
            {settings.data.catalogue.map((l) => (
              <label key={l.code} style={{ display: 'flex', gap: 9, alignItems: 'center', padding: '4px 0', fontWeight: 400 }}>
                <input
                  type="checkbox" style={{ width: 'auto' }}
                  checked={current.includes(l.code)}
                  disabled={!canEdit || l.code === 'en'}
                  onChange={(e) => setChosen(e.target.checked ? [...current, l.code] : current.filter((c) => c !== l.code))}
                />
                <span lang={l.code}>{l.endonym}</span>
                <span className="small muted">{l.english}</span>
                {!l.interfacePack && <Pill tag>No interface pack</Pill>}
              </label>
            ))}
          </div>
          {canEdit && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 12 }}>
              <button className="primary" onClick={save} disabled={!dirty || busy}>{busy ? 'Saving…' : 'Save'}</button>
              <button onClick={() => setChosen(null)} disabled={!dirty}>Discard</button>
              <span className="small muted" style={{ marginInlineStart: 'auto' }}>{current.length} offered</span>
            </div>
          )}
        </>
      )}
    </Card>
  );
}

const SCOPE_LABELS: Array<[string, string, string]> = [
  ['consent:capture', 'Record consent', 'Your servers or apps submit decisions on a person\'s behalf.'],
  ['consent:check', 'Check consent', 'Whether a purpose is granted for a person, before a message or process runs.'],
  ['consent:read', 'Read consent state', 'Full state for one person, for a CRM or a rights request.'],
  ['suppression:read', 'Pull the suppression list', 'Who has withdrawn, for systems that cannot receive a webhook.'],
  ['export:read', 'Download exports', 'Signed exports, for an archive or a regulator.'],
];

interface ServiceKeyRow {
  id: string; name: string; prefix: string; scopes: string[]; status: 'active' | 'revoked';
  createdAt: string; lastUsedAt: string | null;
}

/**
 * Keys the customer's own systems present to the API. Shown once at creation
 * and stored hashed, so a leaked console session cannot recover one.
 */
function ServiceKeysCard() {
  const toast = useToast();
  const keys = useResource<{ keys: ServiceKeyRow[] }>(() => api.get('/v1/service-keys'));
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [scopes, setScopes] = useState<string[]>(['consent:check']);
  const [busy, setBusy] = useState(false);
  const [issued, setIssued] = useState<{ name: string; key: string } | null>(null);

  async function create() {
    setBusy(true);
    try {
      const res = await api.post<{ key: string; name: string }>('/v1/service-keys', { name, scopes });
      setIssued({ name: res.name, key: res.key });
      setCreating(false);
      setName('');
      setScopes(['consent:check']);
      keys.reload();
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  async function revoke(k: ServiceKeyRow) {
    if (!window.confirm(`Revoke "${k.name}"? Systems using it stop working at once.`)) return;
    try {
      await api.post(`/v1/service-keys/${k.id}/revoke`);
      toast('Key revoked.', 'ok');
      keys.reload();
    } catch (err) {
      toast((err as Error).message, 'bad');
    }
  }

  return (
    <Card title="Service keys">
      <ErrorNote error={keys.error} />
      {keys.data && keys.data.keys.length > 0 && (
        <div className="table-scroll">
          <table className="data">
            <thead><tr><th>Name</th><th>Prefix</th><th>Can</th><th>Last used</th><th>Status</th><th /></tr></thead>
            <tbody>
              {keys.data.keys.map((k) => (
                <tr key={k.id}>
                  <td><strong>{k.name}</strong><div className="small muted">Issued {dateTime(k.createdAt)}</div></td>
                  <td className="mono small">{k.prefix}…</td>
                  <td className="small">{k.scopes.map((s) => SCOPE_LABELS.find(([c]) => c === s)?.[1] ?? s).join(', ')}</td>
                  <td className="small muted">{k.lastUsedAt ? dateTime(k.lastUsedAt) : 'never'}</td>
                  <td>{k.status === 'active' ? <Pill tone="ok">Active</Pill> : <Pill>Revoked</Pill>}</td>
                  <td style={{ textAlign: 'right' }}>
                    {k.status === 'active' && <button className="small" onClick={() => revoke(k)}>Revoke</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {keys.data && keys.data.keys.length === 0 && <Empty>No service keys.</Empty>}
      <div style={{ marginTop: 12 }}>
        <button className="primary" onClick={() => setCreating(true)}>Issue key</button>
      </div>

      {creating && (
        <Modal
          title="Issue service key"
          onClose={() => setCreating(false)}
          footer={
            <>
              <button onClick={() => setCreating(false)}>Cancel</button>
              <button className="primary" onClick={create} disabled={busy || !name.trim() || scopes.length === 0}>
                {busy ? 'Issuing…' : 'Issue'}
              </button>
            </>
          }
        >
          <Field label="System">
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Membership CRM" />
          </Field>
          <Field label="Scopes">
            {SCOPE_LABELS.map(([code, label, help]) => (
              <label key={code} style={{ display: 'flex', gap: 9, alignItems: 'flex-start', padding: '5px 0', fontWeight: 400 }}>
                <input
                  type="checkbox" style={{ width: 'auto', marginTop: 3 }}
                  checked={scopes.includes(code)}
                  onChange={(e) => setScopes((s) => (e.target.checked ? [...s, code] : s.filter((x) => x !== code)))}
                />
                <span>{label}<div className="small muted">{help}</div></span>
              </label>
            ))}
          </Field>
        </Modal>
      )}

      {issued && (
        <Modal
          title={`Key for ${issued.name}`}
          description="Shown once."
          onClose={() => setIssued(null)}
          footer={<button className="primary" onClick={() => setIssued(null)}>Done</button>}
        >
          <pre className="mono keyblock">{issued.key}</pre>
          <button
            className="small"
            onClick={() => navigator.clipboard.writeText(issued.key).then(() => toast('Copied.', 'ok'))}
          >
            Copy
          </button>
          <div className="small muted" style={{ marginTop: 10 }}>
            Send it in the <code className="mono">X-Service-Key</code> header, or as <code className="mono">Authorization: Bearer &lt;key&gt;</code>.
          </div>
        </Modal>
      )}
    </Card>
  );
}

interface ChannelRow {
  id: string; name: string; kind: 'smtp' | 'cpaas' | 'relay' | 'api_pull' | 'whatsapp'; medium: 'email' | 'sms' | 'whatsapp'; vendor: string | null;
  fromAddress: string | null; status: 'active' | 'disabled'; isDefault: boolean; summary: string;
  lastTestAt: string | null; lastTestOk: boolean | null; lastTestNote: string | null;
}

const KIND_LABEL: Record<ChannelRow['kind'], string> = {
  smtp: 'Email over SMTP', cpaas: 'SMS via a vendor', relay: 'Your own relay', api_pull: 'Your system pulls and sends',
  whatsapp: 'WhatsApp Business account',
};

/**
 * Where this organisation's email and SMS go. Set up here, tested here, picked
 * by campaigns; one-time codes use the default for each medium.
 */
function ChannelsCard() {
  const toast = useToast();
  const me = getUser();
  const channels = useResource<{ channels: ChannelRow[] }>(() => api.get('/v1/channels'));
  const [adding, setAdding] = useState(false);
  const [testing, setTesting] = useState<string | null>(null);

  async function test(ch: ChannelRow) {
    const to = window.prompt(`Send a test ${ch.medium === 'sms' ? 'SMS' : ch.medium === 'whatsapp' ? 'WhatsApp message' : 'email'} to`, ch.medium === 'email' ? me?.email ?? '' : '');
    if (!to) return;
    setTesting(ch.id);
    try {
      const res = await api.post<{ ok: boolean; note: string }>(`/v1/channels/${ch.id}/test`, { to });
      toast(res.ok ? 'Test delivered to the channel.' : res.note, res.ok ? 'ok' : 'bad');
      channels.reload();
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setTesting(null);
    }
  }

  async function post(path: string, body: unknown, done: string) {
    try {
      await api.post(path, body);
      toast(done, 'ok');
      channels.reload();
    } catch (err) {
      toast((err as Error).message, 'bad');
    }
  }

  return (
    <Card title="Channels">
      <ErrorNote error={channels.error} />
      {channels.data && channels.data.channels.length > 0 && (
        <div className="table-scroll">
          <table className="data">
            <thead><tr><th>Name</th><th>Carries</th><th>Kind</th><th>Last test</th><th>Status</th><th /></tr></thead>
            <tbody>
              {channels.data.channels.map((ch) => (
                <tr key={ch.id}>
                  <td><strong>{ch.name}</strong>{ch.isDefault && <> <Pill tone="brand">Default</Pill></>}<div className="small muted">{ch.summary}{ch.fromAddress ? ` · from ${ch.fromAddress}` : ''}</div></td>
                  <td>{ch.medium === 'sms' ? 'SMS' : ch.medium === 'whatsapp' ? 'WhatsApp' : 'Email'}</td>
                  <td className="small">{KIND_LABEL[ch.kind]}</td>
                  <td className="small">
                    {ch.lastTestAt
                      ? <>{ch.lastTestOk ? <Pill tone="ok">Passed</Pill> : <Pill tone="bad">Failed</Pill>} <span className="muted">{dateTime(ch.lastTestAt)}</span>{!ch.lastTestOk && ch.lastTestNote && <div className="muted">{ch.lastTestNote}</div>}</>
                      : <span className="muted">never</span>}
                  </td>
                  <td>{ch.status === 'active' ? <Pill tone="ok">Active</Pill> : <Pill>Disabled</Pill>}</td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {ch.kind !== 'api_pull' && <button className="small" onClick={() => test(ch)} disabled={testing === ch.id}>{testing === ch.id ? 'Sending…' : 'Test'}</button>}{' '}
                    {!ch.isDefault && ch.status === 'active' && <button className="small" onClick={() => post(`/v1/channels/${ch.id}/default`, {}, 'Default set.')}>Make default</button>}{' '}
                    <button className="small" onClick={() => post(`/v1/channels/${ch.id}/status`, { status: ch.status === 'active' ? 'disabled' : 'active' }, ch.status === 'active' ? 'Disabled.' : 'Enabled.')}>{ch.status === 'active' ? 'Disable' : 'Enable'}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {channels.data && channels.data.channels.length === 0 && (
        <Empty>No channels.</Empty>
      )}
      <div style={{ marginTop: 12 }}>
        <button className="primary" onClick={() => setAdding(true)}>Add channel</button>
      </div>
      {adding && <AddChannel onClose={() => setAdding(false)} onAdded={() => { setAdding(false); channels.reload(); }} />}
    </Card>
  );
}

function AddChannel({ onClose, onAdded }: { onClose: () => void; onAdded: () => void }) {
  const toast = useToast();
  const [kind, setKind] = useState<ChannelRow['kind']>('smtp');
  const [name, setName] = useState('');
  const [fromAddress, setFromAddress] = useState('');
  const [fromName, setFromName] = useState('');
  const [smtp, setSmtp] = useState({ host: '', port: 587, user: '', pass: '' });
  const [cpaas, setCpaas] = useState({ vendor: 'msg91', authKey: '', senderId: '', templateId: '' });
  const [relay, setRelay] = useState({ url: '', token: '' });
  const [wa, setWa] = useState({ phoneNumberId: '', accessToken: '', templateName: '', templateLang: 'en' });
  const [relayMedium, setRelayMedium] = useState<'email' | 'sms' | 'whatsapp'>('email');
  const [isDefault, setIsDefault] = useState(true);
  const [busy, setBusy] = useState(false);
  const medium: 'email' | 'sms' | 'whatsapp' = kind === 'smtp' ? 'email' : kind === 'cpaas' ? 'sms' : kind === 'whatsapp' ? 'whatsapp' : relayMedium;

  async function create() {
    setBusy(true);
    try {
      await api.post('/v1/channels', {
        name, kind, medium, isDefault,
        fromAddress: fromAddress || undefined, fromName: fromName || undefined,
        ...(kind === 'smtp' ? { smtp: { ...smtp, port: Number(smtp.port) } } : {}),
        ...(kind === 'cpaas' ? { cpaas } : {}),
        ...(kind === 'relay' ? { relay: { url: relay.url, token: relay.token || undefined } } : {}),
        ...(kind === 'whatsapp' ? { whatsapp: { provider: 'meta_cloud', ...wa } } : {}),
      });
      toast('Channel added. Send a test before using it.', 'ok');
      onAdded();
    } catch (err) {
      toast((err as Error).message, 'bad');
      setBusy(false);
    }
  }

  const ready = name.trim() && (
    kind === 'smtp' ? smtp.host && fromAddress
    : kind === 'cpaas' ? cpaas.authKey && cpaas.senderId && cpaas.templateId
    : kind === 'relay' ? relay.url
    : kind === 'whatsapp' ? wa.phoneNumberId && wa.accessToken && wa.templateName
    : true);

  return (
    <Modal
      title="Add channel"
      onClose={onClose}
      footer={<><button onClick={onClose}>Cancel</button><button className="primary" onClick={create} disabled={busy || !ready}>{busy ? 'Adding…' : 'Add'}</button></>}
    >
      <Field label="Kind">
        <select value={kind} onChange={(e) => setKind(e.target.value as ChannelRow['kind'])}>
          {Object.entries(KIND_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
      </Field>
      <Field label="Name">
        <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder={kind === 'smtp' ? 'Club mail server' : kind === 'cpaas' ? 'MSG91 transactional' : 'Marketing platform'} />
      </Field>

      {kind === 'smtp' && (
        <>
          <div className="row">
            <Field label="Host"><input type="text" value={smtp.host} onChange={(e) => setSmtp({ ...smtp, host: e.target.value })} placeholder="smtp.example.com" /></Field>
            <Field label="Port" help="465 is implicit TLS; 587 upgrades with STARTTLS."><input type="number" value={smtp.port} onChange={(e) => setSmtp({ ...smtp, port: Number(e.target.value) })} /></Field>
          </div>
          <div className="row">
            <Field label="Username"><input type="text" value={smtp.user} onChange={(e) => setSmtp({ ...smtp, user: e.target.value })} autoComplete="off" /></Field>
            <Field label="Password" help="Stored encrypted. Not shown again."><input type="password" value={smtp.pass} onChange={(e) => setSmtp({ ...smtp, pass: e.target.value })} autoComplete="new-password" /></Field>
          </div>
          <div className="row">
            <Field label="From address"><input type="email" value={fromAddress} onChange={(e) => setFromAddress(e.target.value)} placeholder="privacy@example.com" /></Field>
            <Field label="From name"><input type="text" value={fromName} onChange={(e) => setFromName(e.target.value)} placeholder="Deccan Turf Club" /></Field>
          </div>
        </>
      )}

      {kind === 'cpaas' && (
        <>
          <Field label="Vendor" help="MSG91 is wired today; the others accept details now and send once wired.">
            <select value={cpaas.vendor} onChange={(e) => setCpaas({ ...cpaas, vendor: e.target.value })}>
              {['msg91', 'twilio', 'kaleyra', 'gupshup', 'exotel'].map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </Field>
          <Field label="Auth key" help="From the vendor's dashboard. Stored encrypted."><input type="password" value={cpaas.authKey} onChange={(e) => setCpaas({ ...cpaas, authKey: e.target.value })} autoComplete="new-password" /></Field>
          <div className="row">
            <Field label="Sender id" help="Your DLT-registered header, for example DECTRF."><input type="text" value={cpaas.senderId} onChange={(e) => setCpaas({ ...cpaas, senderId: e.target.value.toUpperCase() })} maxLength={11} /></Field>
            <Field label="DLT template id" help="The approved template the message text fills."><input type="text" value={cpaas.templateId} onChange={(e) => setCpaas({ ...cpaas, templateId: e.target.value })} /></Field>
          </div>
        </>
      )}

      {kind === 'whatsapp' && (
        <>
          <Field label="Phone number id" help="From WhatsApp Manager, under the business phone number."><input type="text" value={wa.phoneNumberId} onChange={(e) => setWa({ ...wa, phoneNumberId: e.target.value.trim() })} /></Field>
          <Field label="Access token" help="A system-user token with messaging permission. Stored encrypted."><input type="password" value={wa.accessToken} onChange={(e) => setWa({ ...wa, accessToken: e.target.value })} autoComplete="new-password" /></Field>
          <div className="row">
            <Field label="Template name" help="An approved template whose body is {{1}}; the message fills it."><input type="text" value={wa.templateName} onChange={(e) => setWa({ ...wa, templateName: e.target.value.trim() })} /></Field>
            <Field label="Template language"><input type="text" value={wa.templateLang} onChange={(e) => setWa({ ...wa, templateLang: e.target.value.trim() })} maxLength={8} /></Field>
          </div>
        </>
      )}

      {kind === 'relay' && (
        <>
          <Field label="Carries">
            <select value={relayMedium} onChange={(e) => setRelayMedium(e.target.value as 'email' | 'sms' | 'whatsapp')}>
              <option value="email">Email</option>
              <option value="sms">SMS</option>
              <option value="whatsapp">WhatsApp</option>
            </select>
          </Field>
          <Field label="URL" help="The console posts one JSON message per send: channel, to, subject, message."><input type="url" value={relay.url} onChange={(e) => setRelay({ ...relay, url: e.target.value })} placeholder="https://gateway.example.com/send" /></Field>
          <Field label="Bearer token" help="Optional. Stored encrypted."><input type="password" value={relay.token} onChange={(e) => setRelay({ ...relay, token: e.target.value })} autoComplete="new-password" /></Field>
        </>
      )}

      {kind === 'api_pull' && (
        <>
          <Field label="Carries">
            <select value={relayMedium} onChange={(e) => setRelayMedium(e.target.value as 'email' | 'sms' | 'whatsapp')}>
              <option value="email">Email</option>
              <option value="sms">SMS</option>
              <option value="whatsapp">WhatsApp</option>
            </select>
          </Field>
          <div className="note">Nothing is sent from here. A campaign that uses this channel exposes the people to contact, each with a fresh link, through the API for a system you already run to send.</div>
        </>
      )}

      <label className="fb-check">
        <input type="checkbox" checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} />
        Use as the default for {medium === 'sms' ? 'SMS' : medium === 'whatsapp' ? 'WhatsApp' : 'email'}
      </label>
    </Modal>
  );
}

interface Controls { requireMfa: boolean; mfaAfterSso: boolean; makerChecker: boolean }

/**
 * The console controls. Turning one on applies at once; turning one off waits
 * for a second person while approvals are on.
 */
function ControlsCard({ canEdit }: { canEdit: boolean }) {
  const toast = useToast();
  const res = useResource<{ controls: Controls }>(() => api.get('/v1/settings/controls'));
  const [busy, setBusy] = useState(false);
  const [held, setHeld] = useState<string | null>(null);
  const c = res.data?.controls;

  async function set(next: Partial<Controls>) {
    setBusy(true);
    try {
      const r = await api.put<{ controls: Controls }>('/v1/settings/controls', next);
      if (heldForApproval(r)) {
        setHeld(r.approval.summary);
        toast('Sent for approval. A second person must approve this change.', 'ok');
      } else {
        setHeld(null);
        toast('Saved.', 'ok');
      }
      res.reload();
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  const row = (key: keyof Controls, label: string, help: string) => (
    <label className="small" style={{ display: 'flex', gap: 8, alignItems: 'flex-start', margin: '4px 0 12px' }}>
      <input
        type="checkbox" style={{ width: 'auto', marginTop: 3 }} checked={!!c?.[key]} disabled={!canEdit || busy || !c}
        onChange={(e) => set({ [key]: e.target.checked })}
      />
      <span><strong>{label}</strong><div className="muted">{help}</div></span>
    </label>
  );

  return (
    <Card title="Sign-in and approvals">
      <ErrorNote error={res.error} />
      {row('requireMfa', 'Require two-factor sign-in', 'Everyone except view-only roles sets up an authenticator app at their next sign-in and uses it every time.')}
      {row('mfaAfterSso', 'Ask for two-factor after single sign-on', 'Off: the identity provider\'s own sign-in policy stands and no second code is asked here.')}
      {row('makerChecker', 'Second person approves sensitive changes', 'Role permission changes, owner and administrator grants, erasures, retention runs and turning these controls off wait on the Approvals page.')}
      {held && <div className="note" role="status"><strong>Sent for approval.</strong> {held}.</div>}
      {!canEdit && <div className="small muted">This role can view these settings but not change them.</div>}
    </Card>
  );
}

function SecurityPage() {
  const me = getUser();
  const key = useResource<{ keyId: string; publicKeyPem: string }>(() => api.get('/v1/exports/public-key'));
  return (
    <>
      <PageHead title="Security" />
      <div className="grid cols-2">
        <div>
          <Card title="Export signing key">
            {key.data ? (
              <>
                <div className="small">Key id <code className="mono">{key.data.keyId}</code></div>
                <pre className="mono keyblock">{key.data.publicKeyPem}</pre>
                <div className="small muted">
                  Public key for independent verification of exports.
                </div>
              </>
            ) : (
              <div className="small muted">Loading…</div>
            )}
          </Card>

          <Card title="Session">
            <div className="small">
              Signed in as <strong>{me?.email}</strong> ({roleLabel(me?.role ?? '')})
              <div className="muted" style={{ marginTop: 6 }}>
                Session is limited to this browser tab and ends when the browser is closed.
              </div>
            </div>
          </Card>
        </div>
        <div>
          <ControlsCard canEdit={can(me, 'security:edit')} />
          <SsoCard canEdit={can(me, 'security:edit')} />
          <RetentionCard canEdit={can(me, 'security:edit')} />
          {can(me, 'requests:view') && <NominationSettingsCard canEdit={can(me, 'requests:configure')} />}
        </div>
      </div>
    </>
  );
}

interface SsoView { issuer: string; clientId: string; emailDomains: string[]; enforce: boolean; status: 'active' | 'disabled'; hasSecret: boolean; updatedBy: string | null; updatedAt: string | null }

/** Sign-in through the organisation's own OpenID Connect identity provider. */
function SsoCard({ canEdit }: { canEdit: boolean }) {
  const toast = useToast();
  const [tick, setTick] = useState(0);
  const res = useResource<{ sso: SsoView | null; redirectUri: string }>(() => api.get('/v1/settings/sso'), [tick]);
  const [form, setForm] = useState<{ issuer: string; clientId: string; clientSecret: string; domains: string; enforce: boolean; status: 'active' | 'disabled' } | null>(null);
  const [busy, setBusy] = useState(false);
  const sso = res.data?.sso ?? null;

  function edit() {
    setForm({ issuer: sso?.issuer ?? '', clientId: sso?.clientId ?? '', clientSecret: '', domains: (sso?.emailDomains ?? []).join(', '), enforce: sso?.enforce ?? false, status: sso?.status ?? 'active' });
  }

  async function save() {
    if (!form) return;
    setBusy(true);
    try {
      await api.put('/v1/settings/sso', {
        issuer: form.issuer.trim(), clientId: form.clientId.trim(), clientSecret: form.clientSecret || undefined,
        emailDomains: form.domains.split(/[\s,]+/).filter(Boolean), enforce: form.enforce, status: form.status,
      });
      toast('Single sign-on saved.', 'ok');
      setForm(null);
      setTick((t) => t + 1);
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title="Single sign-on">
      {res.loading && !res.data && <Loading />}
      <ErrorNote error={res.error} />
      {res.data && !form && (
        sso ? (
          <dl className="kv">
            <dt>Status</dt><dd>{sso.status === 'active' ? <Pill tone="ok">Active</Pill> : <Pill>Disabled</Pill>}</dd>
            <dt>Issuer</dt><dd className="mono small" style={{ wordBreak: 'break-all' }}>{sso.issuer}</dd>
            <dt>Client id</dt><dd className="mono small">{sso.clientId}</dd>
            <dt>Email domains</dt><dd>{sso.emailDomains.join(', ')}</dd>
            <dt>Codes by email</dt><dd>{sso.enforce ? 'Owners only' : 'Allowed'}</dd>
            <dt>Redirect URI</dt><dd className="mono small" style={{ wordBreak: 'break-all' }}>{res.data.redirectUri}</dd>
            {sso.updatedAt && <><dt>Updated</dt><dd>{dateTime(sso.updatedAt)}{sso.updatedBy ? ` · ${sso.updatedBy}` : ''}</dd></>}
          </dl>
        ) : (
          <>
            <Empty>Not set up. People sign in with a code sent by email.</Empty>
            <div className="small muted">Redirect URI to register with the identity provider: <span className="mono" style={{ wordBreak: 'break-all' }}>{res.data.redirectUri}</span></div>
          </>
        )
      )}
      {res.data && canEdit && !form && <button className="small" style={{ marginTop: 10 }} onClick={edit}>{sso ? 'Edit' : 'Set up'}</button>}
      {form && (
        <>
          <Field label="Issuer URL" help="The OpenID Connect issuer, for example https://login.microsoftonline.com/<tenant>/v2.0">
            <input type="url" value={form.issuer} onChange={(e) => setForm({ ...form, issuer: e.target.value })} />
          </Field>
          <Field label="Client id"><input type="text" value={form.clientId} onChange={(e) => setForm({ ...form, clientId: e.target.value })} /></Field>
          <Field label="Client secret" help={sso?.hasSecret ? 'Leave blank to keep the stored secret.' : 'Stored encrypted.'}>
            <input type="password" autoComplete="new-password" value={form.clientSecret} onChange={(e) => setForm({ ...form, clientSecret: e.target.value })} />
          </Field>
          <Field label="Email domains" help="Comma separated. People with these addresses sign in through the provider.">
            <input type="text" value={form.domains} onChange={(e) => setForm({ ...form, domains: e.target.value })} />
          </Field>
          <label className="small" style={{ display: 'flex', gap: 8, alignItems: 'center', margin: '4px 0 10px' }}>
            <input type="checkbox" checked={form.enforce} onChange={(e) => setForm({ ...form, enforce: e.target.checked })} />
            Require single sign-on for these domains. Owners keep email codes.
          </label>
          <Field label="Status">
            <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value as 'active' | 'disabled' })}>
              <option value="active">Active</option>
              <option value="disabled">Disabled</option>
            </select>
          </Field>
          <div className="small muted" style={{ marginBottom: 10 }}>Redirect URI: <span className="mono" style={{ wordBreak: 'break-all' }}>{res.data?.redirectUri}</span></div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => setForm(null)} disabled={busy}>Cancel</button>
            <button className="primary" onClick={save} disabled={busy || !form.issuer || !form.clientId}>{busy ? 'Checking provider…' : 'Save'}</button>
          </div>
        </>
      )}
    </Card>
  );
}

/** How long the identity behind consent records is kept. */
function RetentionCard({ canEdit }: { canEdit: boolean }) {
  const toast = useToast();
  const [tick, setTick] = useState(0);
  const res = useResource<{ due: number; policy: { years: number | null; keepPurposeIds: string[]; lastRunAt: string | null; statementsRequired: boolean } }>(() => api.get('/v1/settings/retention'), [tick]);
  const purposes = useResource<{ purposes: Array<{ id: string; current: { name: string } }> }>(() => api.get('/v1/purposes'), []);
  const [form, setForm] = useState<{ years: string; keep: string[]; statementsRequired: boolean } | null>(null);
  const [busy, setBusy] = useState(false);
  const p = res.data?.policy;
  const nameOf = (id: string) => purposes.data?.purposes.find((x) => x.id === id)?.current.name ?? id;

  async function save() {
    if (!form) return;
    setBusy(true);
    try {
      await api.put('/v1/settings/retention', { years: form.years ? Number(form.years) : null, keepPurposeIds: form.keep, statementsRequired: form.statementsRequired });
      toast('Retention saved.', 'ok');
      setForm(null);
      setTick((t) => t + 1);
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  async function run() {
    if (!res.data || !window.confirm(`Erase the stored identifier of ${res.data.due} principals? Consent events stay in the ledger.`)) return;
    setBusy(true);
    try {
      const r = await api.post<{ erased: number }>('/v1/settings/retention/run');
      toast(heldForApproval(r) ? 'Sent for approval. A second person must approve the run on the Approvals page.' : `${r.erased} identifiers erased.`, 'ok');
      setTick((t) => t + 1);
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title="Consent record retention">
      {res.loading && !res.data && <Loading />}
      <ErrorNote error={res.error} />
      {p && !form && (
        <>
          <dl className="kv">
            <dt>Identity kept for</dt><dd>{p.years ? `${p.years} years after last activity` : 'Kept'}</dd>
            <dt>Always kept</dt><dd>{p.keepPurposeIds.length ? p.keepPurposeIds.map(nameOf).join(', ') : '—'}</dd>
            <dt>Due for erasure</dt><dd>{p.years ? res.data!.due : '—'}</dd>
            <dt>Last run</dt><dd>{p.lastRunAt ? dateTime(p.lastRunAt) : '—'}</dd>
            <dt>Notice statement</dt><dd>{p.statementsRequired ? 'Required before a notice is published' : 'Warned on the checklist, not required'}</dd>
          </dl>
          <div className="small muted" style={{ marginTop: 8 }}>
            Erasure removes the stored identifier. Consent events and the ledger chain stay. Principals with a live consent are not erased. Runs daily.
            {' '}Purposes whose retention period is ending are listed under <Link to="/settings/retention">Retention due</Link>.
          </div>
          {canEdit && (
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <button className="small" onClick={() => setForm({ years: p.years ? String(p.years) : '', keep: p.keepPurposeIds, statementsRequired: p.statementsRequired })}>Edit</button>
              {!!p.years && res.data!.due > 0 && <button className="small danger" onClick={run} disabled={busy}>Erase {res.data!.due} now</button>}
            </div>
          )}
        </>
      )}
      {form && (
        <>
          <Field label="Identity kept for">
            <select value={form.years} onChange={(e) => setForm({ ...form, years: e.target.value })}>
              <option value="">Kept</option>
              {[1, 2, 3, 5, 7, 8, 10, 15, 20].map((y) => <option key={y} value={y}>{y} years after last activity</option>)}
            </select>
          </Field>
          <Field label="Always kept" help="Principals with a consent under these purposes are never erased.">
            <div style={{ display: 'grid', gap: 4 }}>
              {(purposes.data?.purposes ?? []).map((x) => (
                <label key={x.id} className="small" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input type="checkbox" checked={form.keep.includes(x.id)}
                    onChange={(e) => setForm({ ...form, keep: e.target.checked ? [...form.keep, x.id] : form.keep.filter((k) => k !== x.id) })} />
                  {x.current.name}
                </label>
              ))}
            </div>
          </Field>
          <Field label="Where the data is kept" help="A notice covering a purpose with a retention period lists its systems of record, live and backup periods and basis. Required blocks publishing until one row exists; otherwise the checklist warns.">
            <label className="small" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input type="checkbox" checked={form.statementsRequired}
                onChange={(e) => setForm({ ...form, statementsRequired: e.target.checked })} />
              Required before a notice is published
            </label>
          </Field>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => setForm(null)} disabled={busy}>Cancel</button>
            <button className="primary" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
          </div>
        </>
      )}
    </Card>
  );
}

interface NominationSettings {
  max: number; notifyNominee: boolean; twoPerson: boolean;
  noticeDaysDeath: number; noticeDaysIncapacity: number; decisionDays: number; sessionMinutes: number;
}

const NOMINATION_NUMBERS: Array<{ key: 'max' | 'noticeDaysDeath' | 'noticeDaysIncapacity' | 'decisionDays' | 'sessionMinutes'; label: string; min: number; max: number }> = [
  { key: 'max', label: 'Nominees per person', min: 1, max: 5 },
  { key: 'noticeDaysDeath', label: 'Notice to the principal before review (death), days', min: 0, max: 30 },
  { key: 'noticeDaysIncapacity', label: 'Notice to the principal before review (incapacity), days', min: 0, max: 30 },
  { key: 'decisionDays', label: 'Decision due within, days', min: 7, max: 90 },
  { key: 'sessionMinutes', label: 'Nominee session, minutes', min: 5, max: 60 },
];

/** S.14 nominations: limits, notices and the two-person check. */
function NominationSettingsCard({ canEdit }: { canEdit: boolean }) {
  const toast = useToast();
  const [tick, setTick] = useState(0);
  const res = useResource<{ settings: NominationSettings }>(() => api.get('/v1/settings/nomination'), [tick]);
  const [form, setForm] = useState<Record<string, string | boolean> | null>(null);
  const [busy, setBusy] = useState(false);
  const s = res.data?.settings;

  const invalid = form ? NOMINATION_NUMBERS.some(({ key, min, max }) => {
    const v = Number(form[key]);
    return form[key] === '' || !Number.isInteger(v) || v < min || v > max;
  }) : false;

  function edit() {
    if (!s) return;
    setForm({
      max: String(s.max), notifyNominee: s.notifyNominee, twoPerson: s.twoPerson,
      noticeDaysDeath: String(s.noticeDaysDeath), noticeDaysIncapacity: String(s.noticeDaysIncapacity),
      decisionDays: String(s.decisionDays), sessionMinutes: String(s.sessionMinutes),
    });
  }

  async function save() {
    if (!form) return;
    setBusy(true);
    try {
      await api.put('/v1/settings/nomination', {
        max: Number(form.max), notifyNominee: !!form.notifyNominee, twoPerson: !!form.twoPerson,
        noticeDaysDeath: Number(form.noticeDaysDeath), noticeDaysIncapacity: Number(form.noticeDaysIncapacity),
        decisionDays: Number(form.decisionDays), sessionMinutes: Number(form.sessionMinutes),
      });
      toast('Nomination settings saved.', 'ok');
      setForm(null);
      setTick((t) => t + 1);
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  const yesNo = (v: boolean) => (v ? 'Yes' : 'No');

  return (
    <Card title="Nominations (S.14)">
      <div className="nm-settings">
        {res.loading && !res.data && <Loading />}
        <ErrorNote error={res.error} />
        {s && !form && (
          <>
            <dl className="kv">
              <dt>Nominees per person</dt><dd>{s.max}</dd>
              <dt>Tell a nominee when named</dt><dd>{yesNo(s.notifyNominee)}</dd>
              <dt>Two people to approve an activation</dt><dd>{yesNo(s.twoPerson)}</dd>
              <dt>Notice to the principal before review (death), days</dt><dd>{s.noticeDaysDeath}</dd>
              <dt>Notice to the principal before review (incapacity), days</dt><dd>{s.noticeDaysIncapacity}</dd>
              <dt>Decision due within, days</dt><dd>{s.decisionDays}</dd>
              <dt>Nominee session, minutes</dt><dd>{s.sessionMinutes}</dd>
            </dl>
            {canEdit && <div className="nm-actions"><button className="small" onClick={edit}>Edit</button></div>}
          </>
        )}
        {form && (
          <>
            <Field label="Nominees per person" help="1 to 5">
              <input type="number" min={1} max={5} step={1} value={String(form.max)} onChange={(e) => setForm({ ...form, max: e.target.value })} />
            </Field>
            <label className="nm-toggle">
              <input type="checkbox" checked={!!form.notifyNominee} onChange={(e) => setForm({ ...form, notifyNominee: e.target.checked })} />
              Tell a nominee when named
            </label>
            <label className="nm-toggle">
              <input type="checkbox" checked={!!form.twoPerson} onChange={(e) => setForm({ ...form, twoPerson: e.target.checked })} />
              Two people to approve an activation
            </label>
            {NOMINATION_NUMBERS.filter((n) => n.key !== 'max').map(({ key, label, min, max }) => (
              <Field key={key} label={label} help={`${min} to ${max}`}>
                <input type="number" min={min} max={max} step={1} value={String(form[key])} onChange={(e) => setForm({ ...form, [key]: e.target.value })} />
              </Field>
            ))}
            <div className="nm-actions">
              <button onClick={() => setForm(null)} disabled={busy}>Cancel</button>
              <button className="primary" onClick={save} disabled={busy || invalid}>{busy ? 'Saving…' : 'Save'}</button>
            </div>
          </>
        )}
      </div>
    </Card>
  );
}
